import '@angular/common/locales/pt';
import localePt from '@angular/common/locales/pt'; // tslint:disable-line
import jwtDecode from 'jwt-decode';
import { Router } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CharonHttpService, CharonModule } from '@quickweb/connect-angular';
import { LoginOptions, OAuthEvent, OAuthModule, OAuthService } from 'angular-oauth2-oidc';
import { ObservabilidadeInitializeService } from './core/services/analytics/initialization/observabilidade-initialize.service';
import { IdsModalModule, IdsIconModule, IdsAccordionModule, IdsMainButtonModule, IdsLoadingModule, IdsConfigModule } from '@ids/angular';
import { Segments } from '@ids/tools';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ApplicationBottomModule } from './application-bottom/application-bottom.module';
import { ApplicationTopModule } from './application-top/application-top.module';
import { AuthInterceptor } from './auth.interceptor';
import { ContentWrapperModule } from './content-wrapper/content-wrapper.module';
import { CoreModule } from './core/core.module';
import { RouterLoaderHelper } from './core/helpers/router-loader/router-loader.helper';
import { CoreService } from './core/services/core/core.service';
import { LoaderProfileService } from './core/services/loader-profile/loader-profile.service';
import { StoreService } from './core/services/store/store.service';
import { ErrorTreatmentModule } from './error-treatment/error-treatment.module';
import { TermsOfUseModule } from './terms-of-use/terms-of-use.module';

registerLocaleData(localePt, 'pt');
@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ApplicationBottomModule,
    ApplicationTopModule,
    MatDialogModule,
    ErrorTreatmentModule,
    AppRoutingModule,
    ContentWrapperModule,
    CoreModule,
    TermsOfUseModule,
    IdsModalModule,
    IdsIconModule,
    IdsAccordionModule,
    IdsMainButtonModule,
    IdsLoadingModule,
    CharonModule.withConfig({
      provider: CharonHttpService
    }),
    IdsConfigModule.forRoot({
      production: environment.production,
      segment: Segments.Empresas
    }),
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [environment.charonEntrypoint],
        sendAccessToken: false
      }
    })
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: load,
      deps: [OAuthService, Router, LoaderProfileService, CoreService, StoreService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (sdkService: ObservabilidadeInitializeService) => () => {
        sdkService.inicializar().subscribe();
      },
      deps: [ObservabilidadeInitializeService],
      multi: true
    },
    AuthInterceptor,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}

export function load(
  oAuthService: OAuthService,
  router: Router,
  loaderProfile: LoaderProfileService,
  coreService: CoreService,
  storeService: StoreService
) {
  return () =>
    new Promise((resolve, _reject) => {
      initializeApplication(oAuthService, router, loaderProfile, coreService, storeService, resolve).catch(async (error) => {
        router.navigateByUrl('erro', {
          state: {
            error: {
              type: error['type'],
              status: error['status'],
              statusCode: error['statusCode'],
              url: error['url']
            }
          }
        });
        resolve(true);
      });
    });
}

/**
 * Initializes application routes, profile, menu and modal information
 * @param oAuthService
 * @param router
 * @param loaderProfile
 * @param coreService
 * @param storeService
 * @returns Finished promise where the application configs are defined
 */
export async function initializeApplication(
  oAuthService: OAuthService,
  router: Router,
  loaderProfile: LoaderProfileService,
  coreService: CoreService,
  storeService: StoreService,
  resolve: (value: unknown) => void
) {
  let customConfig = {};

  defineTAACEnvironment(storeService, loaderProfile, router, coreService, resolve);
  defineAuthenticatorBasedOnPathname();

  if (sessionStorage.getItem('authenticator') === 'ISAM') {
    oAuthService.configure(environment.isamAuth);
  } else if (sessionStorage.getItem('authenticator') === 'APP_BANKLINE') {
    defineBanklineAuthenticator(oAuthService);
    customConfig = { customHashFragment: '#', disableOAuth2StateCheck: true } as LoginOptions;
  } else if (sessionStorage.getItem('authenticator') === 'CIAM') {
    oAuthService.configure(environment.ciamAuth);
  } else if (sessionStorage.getItem('authenticator') === 'AZUREAD') {
    oAuthService.configure(environment.azureAdAuth);
  }

  if (sessionStorage.getItem('authenticator') !== 'STS') {
    const result = await oAuthService.loadDiscoveryDocumentAndLogin(customConfig);
    if (!result) {
      loaderProfile.denyAccess();
      return false;
    }
    setApplicationToken(oAuthService, storeService);
    defineTokenRefreshEvents(oAuthService, storeService);
    loaderProfile.onLogged();
    setProfile(loaderProfile, router, coreService, storeService, resolve);
  }

  if (location.hash) {
    location.hash = '';
  }
}

function defineTAACEnvironment(
  storeService: StoreService,
  loaderProfile: LoaderProfileService,
  router: Router,
  coreService: CoreService,
  resolve: (value: unknown) => void
) {
  const isTAACEnvironment =
    location.hostname.includes('localhost') || location.hostname.includes('dev') || location.hostname.includes('hom');

  if (!isTAACEnvironment) {
    return;
  }

  const isLocationSTS = window.location.toString().includes('/login/sts');
  const isAuthenticatorSTS = sessionStorage.getItem('authenticator') === 'STS';

  if (!isLocationSTS && !isAuthenticatorSTS) {
    return;
  }

  let token = new URL(document.location.toString()).searchParams.get('token');
  const widgets = new URL(document.location.toString()).searchParams.get('widgets');
  const fileNames = new URL(document.location.toString()).searchParams.get('fileNames');

  if (token == null) {
    token = sessionStorage.getItem('token') || '';
  }

  sessionStorage.setItem('authenticator', 'STS');
  sessionStorage.setItem('widgets', widgets || '');
  sessionStorage.setItem('fileNames', fileNames || '');
  sessionStorage.setItem('token', token);
  storeService.setUserToken(token?.toString());
  storeService.setUserOperator(getOperador('CO', token));

  sessionStorage.setItem('Authorization', token);

  loaderProfile.onLogged();

  if (token) {
    const decoded: { exp: number } = jwtDecode(token);
    const expiration = decoded.exp;
    storeService.setTokenExpiration(+`${expiration}`.padEnd(13, '0'));
  }

  setProfile(loaderProfile, router, coreService, storeService, resolve);
  return;
}

function defineAuthenticatorBasedOnPathname() {
  if (window.location.pathname.toLowerCase() === '/login/isam') {
    sessionStorage.setItem('authenticator', 'ISAM');
  } else if (window.location.pathname.toLowerCase() === '/login/azuread') {
    sessionStorage.setItem('authenticator', 'AZUREAD');
  } else if (window.location.pathname.toLowerCase() === '/callback/app') {
    sessionStorage.setItem('authenticator', 'APP_BANKLINE');
  } else if (window.location.pathname.toLowerCase() === '/login/ciam' || window.location.pathname.toLowerCase() == '/') {
    sessionStorage.setItem('authenticator', 'CIAM');
  }
}

function defineBanklineAuthenticator(oAuthService: OAuthService) {
  const banklineParams = new URLSearchParams(window.location.search);
  environment.ciamAppBanklineAuth.openUri = (uri: any) => {
    const newUrl = new URL(uri);
    newUrl.searchParams.delete('state');
    newUrl.searchParams.delete('nonce');
    newUrl.searchParams.delete('resource');
    newUrl.searchParams.delete('scope');
    location.href = newUrl.toString();
  };

  oAuthService.configure(environment.ciamAppBanklineAuth);

  const params = new URLSearchParams(window.location.hash.substring(1));
  if (params.has('id_token')) {
    const decoded = jwtDecode(params.get('id_token') || '');
    // @ts-ignore
    localStorage.setItem('nonce', decoded['nonce']);
    // @ts-ignore
    sessionStorage.setItem('nonce', decoded['nonce']);
  }

  if (banklineParams.get('request_uri')) {
    oAuthService.customQueryParams = {
      prompt: banklineParams.get('prompt'),
      request_uri: banklineParams.get('request_uri')
    };
  } else if (banklineParams.get('request')) {
    oAuthService.customQueryParams = {
      prompt: banklineParams.get('prompt'),
      request: banklineParams.get('request')
    };
  }
}

function defineTokenRefreshEvents(oAuthService: OAuthService, storeService: StoreService) {
  oAuthService.events.subscribe((evt: OAuthEvent) => {
    if (evt.type === 'token_refreshed') {
      setApplicationToken(oAuthService, storeService);
    }
    if (evt.type === 'token_refresh_error') {
      setTimeout(async () => await oAuthService.refreshToken(), 30000);
    }
  });
}

function getOperador(tokenType: string, token: string | null): string {
  const decoded = jwtDecode(token || '');
  // @ts-ignore
  sessionStorage.setItem('operador', tokenType + ':' + decoded.sub);
  // @ts-ignore
  return decoded.sub;
}

function setProfile(
  loaderProfile: LoaderProfileService,
  router: Router,
  coreService: CoreService,
  storeService: StoreService,
  resolve: (value: unknown) => void
) {
  loaderProfile.isLogged$.subscribe((isLogged: any) => {
    if (!isLogged) {
      router.navigateByUrl('negado');
    }
    const subs = coreService.getApplicationInfo().subscribe(
      async (applicationInfo: any) => {
        if (!applicationInfo) {
          return;
        }

        storeService.setProfile(applicationInfo?.usuario);
        if (applicationInfo?.usuario.empresas.length === 0) {
          loaderProfile.denyAccess();
        }

        defineMenuInformation(applicationInfo, storeService);
        defineWidgetInformation(applicationInfo, storeService);

        await RouterLoaderHelper.setRoutesPaths(applicationInfo?.menu, router);
        loaderProfile.onRouting();

        defineSessionInformation(coreService, storeService);

        resolve(1);
        subs.unsubscribe();
      },
      (error) => {
        loaderProfile.denyAccess();
        resolve(1);
        console.error(error);
      }
    );
  });
}

function defineMenuInformation(applicationInfo: any, storeService: StoreService) {
  if (!applicationInfo?.menu || applicationInfo.menu.menu.length <= 0) {
    return;
  }
  const menuElements = Object.assign({}, applicationInfo?.menu);
  menuElements.menu = applicationInfo?.menu?.menu;
  urlDebugLocalhostReplace(menuElements);
  storeService.setMenu(menuElements);
}

function defineWidgetInformation(applicationInfo: any, storeService: StoreService) {
  if (!applicationInfo?.menu?.widgets) {
    return;
  }

  if (sessionStorage.getItem('authenticator') === 'STS') {
    stsTaacReplaceMainjsWidgetsFileName(applicationInfo.menu.widgets);
  }
  storeService.setWidgetList(applicationInfo.menu.widgets);
}

function defineSessionInformation(coreService: CoreService, storeService: StoreService) {
  validateSession(coreService, storeService);
  const intervalID = setInterval(() => validateSession(coreService, storeService), 60000);
  storeService.validSession.subscribe((sessionStatus) => {
    if (!sessionStatus) {
      clearInterval(intervalID);
    }
  });
}

function stsTaacReplaceMainjsWidgetsFileName(widgets: any): void {
  const widgetsNames = sessionStorage.getItem('widgets')?.split(',') || [];
  const fileNames = sessionStorage.getItem('fileNames')?.split(',') || [];
  for (let index = 0; index < widgetsNames.length; index++) {
    const widgetName = widgetsNames[index];
    widgets.forEach((element: { tag_html: string; url: any }) => {
      if (element.tag_html === widgetName) {
        const urlJsFile = element.url.substring(element.url.lastIndexOf('/') + 1);
        element.url = element.url.replace(urlJsFile, fileNames[index]);
      }
    });
  }
}

/** Verifica se o usuário utilizou autenticação ISAM ou CIAM */

function setApplicationToken(oAuthService: OAuthService, storeService: StoreService) {
  const authISS: any = oAuthService.getIdentityClaims();
  let token: string;
  let tokenExpirationTime: number;
  if (authISS?.iss === environment.isamAuth.issuer) {
    token = oAuthService.getIdToken();
    getOperador('FU', token);
    tokenExpirationTime = oAuthService.getIdTokenExpiration();
    oAuthService.setupAutomaticSilentRefresh({ timeoutFactor: 0.7 });
  } else if (authISS?.iss === environment.azureAdAuth.issuer) {
    token = oAuthService.getAccessToken();
    getOperador('FU', token);
    tokenExpirationTime = oAuthService.getIdTokenExpiration();
    oAuthService.setupAutomaticSilentRefresh({ timeoutFactor: 0.7 }, 'access_token');
  } else {
    token = oAuthService.getAccessToken();
    getOperador('CO', token);
    tokenExpirationTime = oAuthService.getAccessTokenExpiration();
    oAuthService.setupAutomaticSilentRefresh({ timeoutFactor: 0.7 }, 'access_token');
  }

  sessionStorage.setItem('Authorization', token);
  storeService.setUserToken(token);
  storeService.setTokenExpiration(tokenExpirationTime);
}

function validateSession(coreService: CoreService, storeService: StoreService) {
  coreService.validateSession().subscribe({
    error(err) {
      if (err && (err.status === 401 || err.status === 403 || err.errorObject.status === 401 || err.errorObject.status === 403)) {
        storeService.setValidSession(false);
      }
    }
  });
}

function urlDebugLocalhostReplace(_menuElements: any) {
  if (location.hostname === 'localhost') {
    // const wds = menuElements.widgets;
    // const item8 = wds.find((x: { tag_html: string }) => x.tag_html === 'mfe-shell-formalizacao-cambio');
    // item8.url = 'http://127.0.0.1:9992/main.js';
    // const item4 = wds.find((x: { tag_html: string }) => x.tag_html === 'mfe-dados-mercado');
    // item4.url = 'http://127.0.0.1:9994/main.js';
  }
}
